import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { Plus, Flame, Calendar, Layers, Package, ChevronRight, CheckCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import api from '../services/api';
import { toast } from 'sonner';
import { format, parse } from 'date-fns';
import { cs } from 'date-fns/locale';

const AutoclavePage = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [devices, setDevices] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    device_id: '',
    program_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recordsRes, devicesRes, programsRes] = await Promise.all([
        api.get('/autoclave/records'),
        api.get('/autoclave/devices'),
        api.get('/autoclave/programs')
      ]);
      setRecords(recordsRes.data);
      setDevices(devicesRes.data);
      setPrograms(programsRes.data);
    } catch (error) {
      toast.error('Chyba při načítání dat');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      date: format(new Date(), 'yyyy-MM-dd'),
      device_id: devices[0]?.id || '',
      program_id: programs[0]?.id || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.device_id || !formData.program_id) {
      toast.error('Vyberte autokláv a program');
      return;
    }

    try {
      const res = await api.post('/autoclave/records', formData);
      toast.success('Záznam vytvořen');
      setDialogOpen(false);
      navigate(`/autoklav/${res.data.id}`);
    } catch (error) {
      toast.error('Chyba při vytváření záznamu');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = parse(dateStr, 'yyyy-MM-dd', new Date());
      return format(date, 'd. M. yyyy', { locale: cs });
    } catch {
      return dateStr;
    }
  };

  const parsedDate = React.useMemo(() => {
    if (!formData.date) return undefined;
    try {
      return parse(formData.date, 'yyyy-MM-dd', new Date());
    } catch {
      return undefined;
    }
  }, [formData.date]);

  const handleCalendarSelect = (date) => {
    if (date) {
      setFormData({ ...formData, date: format(date, 'yyyy-MM-dd') });
    }
    setCalendarOpen(false);
  };

  return (
    <Layout>
      <div className="space-y-6 py-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Autokláv</h1>
            <p className="text-slate-500 mt-1 text-sm sm:text-base">Evidence procesů autoklávování</p>
          </div>
          <Button onClick={handleOpenDialog} className="bg-teal-600 hover:bg-teal-700" disabled={devices.length === 0 || programs.length === 0}>
            <Plus className="h-4 w-4 mr-2" />
            Nový záznam
          </Button>
        </div>

        {/* Warning if no devices or programs */}
        {(devices.length === 0 || programs.length === 0) && !loading && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-amber-800">
              Pro vytvoření záznamu je nutné nejprve přidat{' '}
              {devices.length === 0 && (
                <Button variant="link" className="text-amber-800 underline p-0 h-auto" onClick={() => navigate('/admin/autoclave-devices')}>
                  autoklávy
                </Button>
              )}
              {devices.length === 0 && programs.length === 0 && ' a '}
              {programs.length === 0 && (
                <Button variant="link" className="text-amber-800 underline p-0 h-auto" onClick={() => navigate('/admin/autoclave-programs')}>
                  programy
                </Button>
              )}
              {' '}v administraci.
            </p>
          </div>
        )}

        {/* Records list */}
        {loading ? (
          <div className="text-center py-12">Načítání...</div>
        ) : records.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-teal-100 flex items-center justify-center">
              <Flame className="h-8 w-8 text-teal-600" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">Žádné záznamy</h3>
            <p className="text-slate-500 mb-4">Začněte vytvořením prvního záznamu autoklávu</p>
            <Button onClick={handleOpenDialog} className="bg-teal-600 hover:bg-teal-700" disabled={devices.length === 0 || programs.length === 0}>
              <Plus className="h-4 w-4 mr-2" />
              Nový záznam
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {records.map((record) => (
              <div
                key={record.id}
                onClick={() => navigate(`/autoklav/${record.id}`)}
                className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 hover:shadow-md transition-all cursor-pointer group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      record.status === 'completed' 
                        ? 'bg-gradient-to-br from-green-100 to-emerald-100' 
                        : 'bg-gradient-to-br from-teal-100 to-cyan-100'
                    }`}>
                      <Flame className={`h-6 w-6 ${
                        record.status === 'completed' ? 'text-green-600' : 'text-teal-600'
                      }`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-mono rounded">
                          #{record.record_number || '-'}
                        </span>
                        <h3 className="font-semibold text-slate-900 group-hover:text-teal-700 transition-colors">
                          {record.device_number} - {record.device_name}
                        </h3>
                        <Badge variant="secondary" className={
                          record.status === 'completed' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-amber-100 text-amber-700'
                        }>
                          {record.status === 'completed' ? 'Dokončeno' : 'Probíhá'}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {formatDate(record.date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {record.created_at ? format(new Date(record.created_at), 'HH:mm', { locale: cs }) : '-'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Layers className="h-4 w-4" />
                          Program {record.program_number}: {record.program_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Package className="h-4 w-4" />
                          {record.food_items?.length || 0} jídel
                        </span>
                        <span className="flex items-center gap-1">
                          {record.status === 'completed' ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <Clock className="h-4 w-4 text-amber-500" />
                          )}
                          {record.process_entries?.length || 0} záznamů procesu
                        </span>
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-teal-600 transition-colors" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Record Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nový záznam autoklávu</DialogTitle>
            <DialogDescription>
              Vytvořte nový záznam pro evidenci procesu autoklávování
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Datum *</Label>
              <Button
                variant="outline"
                onClick={() => setCalendarOpen(!calendarOpen)}
                className="w-full justify-start text-left font-normal"
                type="button"
              >
                <Calendar className="h-4 w-4 mr-2" />
                {formatDate(formData.date)}
              </Button>
              {calendarOpen && (
                <div className="border rounded-lg p-3 bg-white shadow-sm">
                  <CalendarComponent
                    mode="single"
                    selected={parsedDate}
                    onSelect={handleCalendarSelect}
                    locale={cs}
                    className="rounded-md"
                  />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Autokláv *</Label>
              <Select
                value={formData.device_id}
                onValueChange={(value) => setFormData({ ...formData, device_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Vyberte autokláv" />
                </SelectTrigger>
                <SelectContent>
                  {devices.map((device) => (
                    <SelectItem key={device.id} value={device.id}>
                      {device.number} - {device.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Program *</Label>
              <Select
                value={formData.program_id}
                onValueChange={(value) => setFormData({ ...formData, program_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Vyberte program" />
                </SelectTrigger>
                <SelectContent>
                  {programs.map((program) => (
                    <SelectItem key={program.id} value={program.id}>
                      {program.number} - {program.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Zrušit</Button>
            <Button onClick={handleSubmit} className="bg-teal-600 hover:bg-teal-700">
              Vytvořit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AutoclavePage;
